/**
 * Sparkline — a tiny inline SVG trend. Overlays two monthly series on a shared
 * y-scale: `actual` (solid) and, when given, `expected` (dashed, muted). The
 * gap between them — and which line sits on top — explains the sign of the
 * site's deviation. No axes/labels (table-cell sized); a11y via aria-label.
 * (No nzds sparkline exists — same rationale as the E2 drill's SVG bars.)
 */
import type React from "react"

export interface SparklineProps {
  actual: readonly number[]
  expected?: readonly number[]
  width?: number
  height?: number
  stroke: string
  strokeWidth?: number
}

export const Sparkline: React.FC<SparklineProps> = ({
  actual,
  expected,
  width = 88,
  height = 30,
  stroke,
  strokeWidth = 1.6,
}) => {
  if (actual.length < 2) {
    return null
  }
  const pad = 3
  // Shared scale across both series so the gap is faithful.
  const all = [...actual, ...(expected ?? [])]
  const min = Math.min(...all)
  const max = Math.max(...all)
  const span = max - min || 1

  const toPoints = (series: readonly number[]): string =>
    series
      .map((value, i) => {
        const x = pad + i * ((width - pad * 2) / (series.length - 1))
        // Invert: higher value → higher on screen (smaller y).
        const y = pad + (1 - (value - min) / span) * (height - pad * 2)
        return `${x.toFixed(1)},${y.toFixed(1)}`
      })
      .join(" ")

  return (
    <svg
      aria-label="Monthly actual vs expected load"
      height={height}
      role="img"
      viewBox={`0 0 ${width} ${height}`}
      width={width}
    >
      {expected && expected.length >= 2 ? (
        <polyline
          data-testid="sparkline-expected"
          fill="none"
          points={toPoints(expected)}
          stroke="currentColor"
          strokeDasharray="3 2"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.2}
          style={{ color: "#94a3b8" }}
        />
      ) : null}
      <polyline
        data-testid="sparkline-actual"
        fill="none"
        points={toPoints(actual)}
        stroke={stroke}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={strokeWidth}
      />
    </svg>
  )
}
